import React, { useCallback, useRef } from 'react';

/**
 * ScoreRadioGroup — accessible 1–5 score selector.
 *
 * Replaces the styled-<button> score row in OpticsScoringForm.
 * Fixes audit #23 (radiogroup semantic + arrow-key nav + aria-checked).
 *
 * Pattern: W3C ARIA radiogroup with roving tabindex.
 *  - Only the focused (or first, if none) option is tab-stop
 *  - ArrowLeft / ArrowUp moves to previous (no wrap)
 *  - ArrowRight / ArrowDown moves to next (no wrap)
 *  - Home / End jumps to 1 / 5
 *  - Space / Enter (handled by native button click) confirms selection
 *
 * Activation model: "automatic" — moving focus updates the value.
 * This matches the OPTICS use-case: users tend to scrub through scores.
 *
 * Tap target: min-h-12 / min-w-12 (48 px), comfortable on mobile.
 */

export const DEFAULT_OPTICS_LEGEND: Readonly<Record<number, string>> = {
  1: 'Below Standard',
  2: 'Inconsistent',
  3: 'Meets Expectations',
  4: 'Strong',
  5: 'Role Model',
};

export interface ScoreRadioGroupProps {
  /** Visible label, e.g. "Individual contribution" */
  label: string;
  /** Sub-text under the label */
  description?: string;
  /** Currently selected score, or undefined */
  value?: number;
  /** Called with new score 1–5 */
  onChange: (score: number) => void;
  /** Optional legend override; defaults to OPTICS 5-point scale */
  legend?: Readonly<Record<number, string>>;
  /** Disabled state */
  disabled?: boolean;
}

const ScoreRadioGroup: React.FC<ScoreRadioGroupProps> = ({
  label,
  description,
  value,
  onChange,
  legend = DEFAULT_OPTICS_LEGEND,
  disabled = false,
}) => {
  const groupRef = useRef<HTMLDivElement>(null);

  const focusScore = useCallback((score: number) => {
    const btn = groupRef.current?.querySelector<HTMLButtonElement>(`[data-score="${score}"]`);
    btn?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, score: number) => {
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = Math.min(5, score + 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = Math.max(1, score - 1);
          break;
        case 'Home':
          next = 1;
          break;
        case 'End':
          next = 5;
          break;
      }
      if (next !== null && next !== score) {
        e.preventDefault();
        onChange(next);
        focusScore(next);
      }
    },
    [onChange, focusScore],
  );

  return (
    <div className="bg-stone-50 rounded-xl p-4">
      <div className="flex justify-between items-start gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-base font-medium text-stone-900">{label}</div>
          {description && (
            <div className="text-sm text-stone-500 leading-snug">{description}</div>
          )}
        </div>
        {typeof value === 'number' && (
          <div className="text-sm text-emerald-700 font-medium shrink-0" aria-hidden="true">
            {legend[value]}
          </div>
        )}
      </div>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={label}
        aria-disabled={disabled || undefined}
        className="flex gap-2"
      >
        {[1, 2, 3, 4, 5].map(score => {
          const isSelected = value === score;
          // Roving tabindex: selected option is tab-stop; if no selection, first is.
          const isTabbable = typeof value === 'number' ? isSelected : score === 1;
          return (
            <button
              key={score}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${score} — ${legend[score]}`}
              data-score={score}
              tabIndex={isTabbable ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(score)}
              onKeyDown={e => handleKeyDown(e, score)}
              className={[
                'flex-1 min-h-12 min-w-12 text-base font-semibold rounded-xl border',
                'transition-colors disabled:opacity-40 disabled:pointer-events-none',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                isSelected
                  ? 'bg-stone-900 text-white border-stone-900 focus-visible:outline-stone-900'
                  : 'bg-white text-stone-800 border-stone-300 hover:border-stone-500 focus-visible:outline-stone-500',
              ].join(' ')}
            >
              {score}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreRadioGroup;
